/**
 * Utilidades Contables y Taxonomía de Conceptos de Pago
 * Divide los conceptos en:
 * - CATEGORÍA A: Conceptos Contables de Residente (afectan adeudos y saldo a favor según su regla).
 * - CATEGORÍA B: Conceptos Misceláneos de Tesorería (no afectan adeudo ni saldo a favor).
 */

export const esConceptoMantenimiento = (concepto) => {
    const c = String(concepto || '').toLowerCase().trim();
    return (
        (c.includes('cuota') && (c.includes('mantenimiento') || c.includes('mantto')) && !c.includes('extraordinaria')) ||
        (c === 'mantenimiento') ||
        (c.includes('pago') && c.includes('anual')) ||
        (c.includes('descuento') || c.includes('pronto pago')) ||
        (c.includes('adeudo') && (c.includes('anterior') || c.includes('pendiente')))
    );
};

export const esConceptoRecargos = (concepto) => {
    const c = String(concepto || '').toLowerCase().trim();
    return (c.includes('recargo') || c.includes('interes')) && !c.includes('cuota') && !c.includes('mantenimiento');
};

export const esConceptoCuotaExtra = (concepto, cuotaExtraId) => {
    const c = String(concepto || '').toLowerCase().trim();
    return Boolean(cuotaExtraId) || (c.includes('extraordinaria') || (c.includes('cuota') && c.includes('extra')));
};

export const esConceptoSancion = (concepto, multaAdminId) => {
    const c = String(concepto || '').toLowerCase().trim();
    return Boolean(multaAdminId) || (
        c.includes('sancion') || 
        c.includes('sanción') || 
        c.includes('sansion') || 
        c.includes('sansión') || 
        c.includes('multa')
    );
};

export const esConceptoMulta = esConceptoSancion;

export const esConceptoAgua = (concepto, periodoAguaId) => {
    const c = String(concepto || '').toLowerCase().trim();
    return Boolean(periodoAguaId) || (c.includes('agua') || c.includes('cea'));
};

/**
 * Determina si una partida o concepto pertenece a la CATEGORÍA A (Contabilidad de Residente).
 * Si retorna false, la partida es CATEGORÍA B (Misceláneo: no toca adeudo ni saldo a favor).
 */
export const esPartidaContableResidente = (partida) => {
    if (!partida) return false;
    const c = typeof partida === 'string' ? partida : (partida.concepto || '');
    const cuotaExtraId = typeof partida === 'object' ? partida.cuotaExtraId : null;
    const multaAdminId = typeof partida === 'object' ? partida.multaAdminId : null;
    const periodoAguaId = typeof partida === 'object' ? partida.periodoAguaId : null;

    return (
        esConceptoMantenimiento(c) ||
        esConceptoRecargos(c) ||
        esConceptoCuotaExtra(c, cuotaExtraId) ||
        esConceptoMulta(c, multaAdminId) ||
        esConceptoAgua(c, periodoAguaId)
    );
};

/**
 * Determina si un concepto pertenece a la CATEGORÍA B (Ingreso de Tesorería).
 * Es el complemento estricto de la Lista Blanca A.
 */
export const esConceptoTesoreria = (concepto) => {
    return !esPartidaContableResidente(concepto);
};

/**
 * Obtiene la información visual y operativa de la categoría del concepto
 */
export const getCategoriaConceptoInfo = (concepto) => {
    const esContable = esPartidaContableResidente(concepto);
    if (esContable) {
        return {
            categoria: 'A',
            nombre: 'Contable Residente',
            afectaAdeudo: true,
            badgeColor: '#1d4ed8',
            badgeBg: '#eff6ff',
            badgeBorder: '#bfdbfe',
            label: 'Categoría A • Afecta Adeudo / Saldo',
            shortLabel: 'Contable Residente'
        };
    }
    return {
        categoria: 'B',
        nombre: 'Ingreso de Tesorería',
        afectaAdeudo: false,
        badgeColor: '#b45309',
        badgeBg: '#fffbeb',
        badgeBorder: '#fde68a',
        label: 'Categoría B • Tesorería (No afecta adeudo)',
        shortLabel: 'Ingreso de Tesorería'
    };
};

/**
 * Determina si una partida puede generar saldo a favor en caso de sobrepago.
 * Por regla estricta: ÚNICAMENTE conceptos de mantenimiento ordinario / anual / pronto pago / adeudos anteriores.
 * Recargos, Extras, Multas, Agua y Misceláneos (Categoría B) tienen PROHIBIDO generar saldo a favor.
 */
export const puedePartidaGenerarSaldoFavor = (partida, metodoPago) => {
    if (metodoPago === 'saldo_favor') return false;
    if (!partida) return false;
    const concepto = typeof partida === 'string' ? partida : partida.concepto;
    return esConceptoMantenimiento(concepto);
};

